import React, { useState, useEffect, useCallback } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { FileDownload, FileUpload, Save, Shield } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AuthorityConfirmModal from "../modals/AuthorityConfirmModal";
import AuthorityUploadModal from "../modals/AuthorityUploadModal";
import api from "../../../../api/axios";

export type UserRoleType = 'SYSTEM_ADMIN' | 'ADMIN' | 'SUPPORT' | 'USER' | 'INFLUENCER';

export interface AuthorityDTO {
    id?: number;
    area: string;
    name: string;
    assignedRoles: string[];
}

export interface UpdateAuthorityRequestDTO {
    authorityName: string;
    roles: string[];
}

export interface RolePermissionState {
    systemAdmin: boolean;
    admin: boolean;
    support: boolean;
    user: boolean;
    influencer: boolean;
}

export interface RoleAuthorityRow extends RolePermissionState {
    id: string;
    rawId?: number;
    category: string;
    authority: string;
}

export interface InternalRoleRow extends RoleAuthorityRow {
    initialState: RolePermissionState;
}

const PRIMARY_COLOR = '#3f51b5';

const ROLE_COLUMNS: { key: keyof RolePermissionState; label: string; roleType: UserRoleType; dbName: string }[] = [
    { key: 'systemAdmin', label: 'System Admin', roleType: 'SYSTEM_ADMIN', dbName: 'SA' },
    { key: 'admin', label: 'Admin', roleType: 'ADMIN', dbName: 'A' },
    { key: 'support', label: 'Support', roleType: 'SUPPORT', dbName: 'S' },
    { key: 'user', label: 'User', roleType: 'USER', dbName: 'U' },
    { key: 'influencer', label: 'Influencer', roleType: 'INFLUENCER', dbName: 'I' },
];

const isRoleInAssigned = (assignedRoles: string[], col: typeof ROLE_COLUMNS[0]): boolean => {
    return assignedRoles.some(
        (role) => role.toUpperCase() === col.dbName || role.toUpperCase() === col.roleType
    );
};

const RolesAuthorities: React.FC = () => {
    const [data, setData] = useState<InternalRoleRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);

    // Fetches authority records from backend API
    const fetchAuthorities = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get<AuthorityDTO[]>('/authorities');

            const mappedRows: InternalRoleRow[] = response.data.map((item, index) => {
                const assigned = item.assignedRoles || [];

                const roleState: RolePermissionState = {
                    systemAdmin: isRoleInAssigned(assigned, ROLE_COLUMNS[0]),
                    admin: isRoleInAssigned(assigned, ROLE_COLUMNS[1]),
                    support: isRoleInAssigned(assigned, ROLE_COLUMNS[2]),
                    user: isRoleInAssigned(assigned, ROLE_COLUMNS[3]),
                    influencer: isRoleInAssigned(assigned, ROLE_COLUMNS[4]),
                };

                return {
                    id: `auth-${item.id ?? index}`,
                    rawId: item.id,
                    category: item.area,
                    authority: item.name,
                    ...roleState,
                    initialState: { ...roleState },
                };
            });

            setData(mappedRows);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to fetch authorities');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAuthorities();
    }, [fetchAuthorities]);

    // Updates checkbox selection state for specified authority row
    const handleCheckboxChange = (id: string, roleKey: keyof RolePermissionState) => {
        setData((prev) =>
            prev.map((row) => (row.id === id ? { ...row, [roleKey]: !row[roleKey] } : row))
        );
    };

    // Persists modified authority records and updates baseline state
    const handleSaveConfirm = async (mfaCode?: string) => {
        setSaving(true);
        try {
            const modifiedRows = data.filter(isRowModified);

            const requests: UpdateAuthorityRequestDTO[] = modifiedRows.map((row) => {
                const roles: string[] = [];
                ROLE_COLUMNS.forEach((col) => {
                    if (row[col.key]) {
                        roles.push(col.dbName);
                    }
                });

                return {
                    authorityName: row.authority,
                    roles, // Vorher: assignedRoles
                };
            });

            const headers: Record<string, string> = {};
            if (mfaCode) {
                headers['X-MFA-Code'] = mfaCode;
            }

            await api.patch('/authorities', requests, { headers });

            toast.success('Authorities updated successfully!');

            setData((prev) =>
                prev.map((row) => ({
                    ...row,
                    initialState: {
                        systemAdmin: row.systemAdmin,
                        admin: row.admin,
                        support: row.support,
                        user: row.user,
                        influencer: row.influencer,
                    },
                }))
            );
            setConfirmModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || error?.response?.data || 'Failed to update authorities');
        } finally {
            setSaving(false);
        }
    };

    // Downloads the authority matrix as an Excel file
    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await api.get('/download-authorities', {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'roles_authorities.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel downloaded successfully');
        } catch (error: any) {
            toast.error('Failed to download authority file');
        } finally {
            setDownloading(false);
        }
    };

    // Handles authority Excel upload with MFA verification
    const handleUploadConfirm = async (file: File, mfaCode?: string) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const headers: Record<string, string> = {
                'Content-Type': 'multipart/form-data',
            };
            if (mfaCode) {
                headers['X-MFA-Code'] = mfaCode;
            }

            await api.post('/upload-authorities', formData, { headers });

            toast.success('Excel uploaded successfully!');
            setUploadModalOpen(false);
            await fetchAuthorities();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || error?.response?.data || 'Failed to upload Excel file');
        } finally {
            setUploading(false);
        }
    };

    // Checks if specific role permission is modified from initial state
    const isFieldModified = (row: InternalRoleRow, roleKey: keyof RolePermissionState) => {
        return row[roleKey] !== row.initialState[roleKey];
    };

    // Checks if authority row contains any role modifications
    const isRowModified = (row: InternalRoleRow) => {
        return ROLE_COLUMNS.some((col) => isFieldModified(row, col.key));
    };

    const totalModifiedCount = data.filter(isRowModified).length;

    return (
        <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh] h-[88vh] overflow-hidden">
            <div className="flex flex-row justify-between items-center gap-2 mb-4 shrink-0">
                <div className="flex items-center gap-2">
                    <Shield style={{ color: PRIMARY_COLOR }} />
                    <h2 className="text-sm md:text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
                        Roles & Authorities
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outlined"
                        onClick={handleDownload}
                        disabled={downloading}
                        startIcon={<FileDownload />}
                        className="h-[36px] px-3 md:px-4 text-[10px] font-black uppercase tracking-widest"
                        sx={{ borderRadius: 0, borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR, '&:hover': { borderColor: '#303f9f' } }}
                    >
                        {downloading ? 'Downloading...' : 'Download Excel'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setUploadModalOpen(true)}
                        startIcon={<FileUpload />}
                        className="h-[36px] px-3 md:px-4 text-[10px] font-black uppercase tracking-widest"
                        sx={{ borderRadius: 0, borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR, '&:hover': { borderColor: '#303f9f' } }}
                    >
                        Upload Excel
                    </Button>
                </div>
            </div>

            <Divider className="!border-gray-100 dark:!border-white/5 mb-4" />

            <TableContainer
                component={Paper}
                className="flex-1 bg-transparent shadow-none border-none overflow-y-auto relative"
                sx={{ borderRadius: 0 }}
            >
                {loading && data.length === 0 ? (
                    <div className="flex justify-center items-center h-48">
                        <CircularProgress sx={{ color: PRIMARY_COLOR }} />
                    </div>
                ) : (
                    <Table size="small" stickyHeader className="w-full border-separate border-spacing-y-2">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ border: 'none', width: '130px' }}
                                    className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white dark:bg-neutral-900 py-3 z-10"
                                >
                                    Category
                                </TableCell>
                                <TableCell
                                    sx={{ border: 'none', width: '210px' }}
                                    className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white dark:bg-neutral-900 py-3 z-10"
                                >
                                    Authority
                                </TableCell>
                                {ROLE_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align="center"
                                        sx={{ border: 'none' }}
                                        className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white dark:bg-neutral-900 py-3 z-10"
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => {
                                const modified = isRowModified(row);

                                return (
                                    <TableRow
                                        key={row.id}
                                        className="group bg-gray-50/30 dark:bg-neutral-900/20 transition-all hover:bg-white/[0.03]"
                                    >
                                        <TableCell sx={{ border: 'none', width: '130px' }}>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    style={{
                                                        width: 3,
                                                        height: 24,
                                                        backgroundColor: modified ? PRIMARY_COLOR : 'transparent',
                                                    }}
                                                    className="rounded-full transition-colors shrink-0"
                                                />
                                                <span className="font-bold text-gray-200 text-xs leading-tight truncate">
                                                    {row.category}
                                                </span>
                                            </div>
                                        </TableCell>

                                        <TableCell sx={{ border: 'none', width: '210px' }}>
                                            <span className="text-[11px] text-gray-400 font-mono truncate block">
                                                {row.authority}
                                            </span>
                                        </TableCell>

                                        {ROLE_COLUMNS.map((col) => {
                                            const isMod = isFieldModified(row, col.key);
                                            return (
                                                <TableCell key={col.key} align="center" sx={{ border: 'none' }}>
                                                    <Tooltip
                                                        title={isMod ? `Initial: ${row.initialState[col.key] ? 'Checked' : 'Unchecked'}` : ''}
                                                        arrow
                                                    >
                                                        <div
                                                            className={`inline-flex items-center justify-center p-0.5 rounded transition-all ${
                                                                isMod ? 'bg-[#3f51b5]/15 ring-1 ring-[#3f51b5]' : ''
                                                            }`}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={row[col.key]}
                                                                onChange={() => handleCheckboxChange(row.id, col.key)}
                                                                sx={{
                                                                    color: isMod ? PRIMARY_COLOR : '#444',
                                                                    '&.Mui-checked': { color: PRIMARY_COLOR },
                                                                }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <div className="flex justify-between items-center pt-4 border-t border-white/5 mt-auto shrink-0">
                <div className="text-[10px] text-gray-500 uppercase font-black">
                    Total Items: {data.length} {totalModifiedCount > 0 && `| Modified: ${totalModifiedCount}`}
                </div>

                <Button
                    variant="contained"
                    onClick={() => setConfirmModalOpen(true)}
                    disabled={saving || totalModifiedCount === 0}
                    className="h-[36px] px-6 text-[10px] font-black uppercase tracking-widest shadow-none"
                    sx={{ borderRadius: 0, backgroundColor: PRIMARY_COLOR, '&:hover': { backgroundColor: '#303f9f' } }}
                    startIcon={<Save />}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </div>

            <AuthorityConfirmModal
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleSaveConfirm}
                modifiedCount={totalModifiedCount}
                loading={saving}
            />

            <AuthorityUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onConfirm={handleUploadConfirm}
                loading={uploading}
            />
        </div>
    );
};

export default RolesAuthorities;